// frontend/src/config/index.js
// ============================================================
// PAWNI Frontend Configuration
// All API URLs, feature flags, and constants.
// ============================================================

// ── API ────────────────────────────────────────────────────
// Vite inlines VITE_API_BASE_URL at build time (VITE_* is public in the bundle).
// Set VITE_API_BASE_URL in Vercel for Production and Preview to https://...
// Runtime logic below forces HTTPS when the page is HTTPS to avoid Mixed Content.

const _RAW_API_URL = (
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8000"
).replace(/\/+$/, "");

// Upgrade http -> https when the page itself is served over https
// (avoids Mixed Content). No hostname special-casing.
export const API_BASE_URL = (() => {
  if (typeof window === "undefined") return _RAW_API_URL;
  const isSecure = window.location?.protocol === "https:";
  if (isSecure && _RAW_API_URL.startsWith("http://")) {
    return _RAW_API_URL.replace("http://", "https://");
  }
  return _RAW_API_URL;
})();

export const API_ENDPOINTS = {
  // Auth
  signup: `${API_BASE_URL}/api/auth/signup`,
  login: `${API_BASE_URL}/api/auth/login`,
  logout: `${API_BASE_URL}/api/auth/logout`,
  refresh: `${API_BASE_URL}/api/auth/refresh`,
  me: `${API_BASE_URL}/api/auth/me`,

  // Sessions
  sessions: `${API_BASE_URL}/api/sessions`,
  session: (id) => `${API_BASE_URL}/api/sessions/${id}`,
  sessionMessages: (id) => `${API_BASE_URL}/api/sessions/${id}/messages`,

  // Chat / pipeline
  chatMessage: (id) => `${API_BASE_URL}/api/chat/${id}/message`,
  chatAdvance: (id) => `${API_BASE_URL}/api/chat/${id}/advance`,
  chatCancel: (id) => `${API_BASE_URL}/api/chat/${id}/cancel`,
  chatRevert: (id) => `${API_BASE_URL}/api/chat/${id}/revert`,
  chatRerun: (id) => `${API_BASE_URL}/api/chat/${id}/rerun`,
  chatStatus: (id) => `${API_BASE_URL}/api/chat/${id}/status`,
  chatResult: (id) => `${API_BASE_URL}/api/chat/${id}/result`,
  chatEvents: (id) => `${API_BASE_URL}/api/chat/${id}/events`,

  // Health / public
  health: `${API_BASE_URL}/health`,
  publicConfig: `${API_BASE_URL}/api/public/config`,
};

// ── Supabase (for frontend auth only) ──────────────────────

export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";
export const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

// ── Feature Flags ──────────────────────────────────────────

export const FEATURES = {
  speechToText: import.meta.env.VITE_ENABLE_STT !== "false",
  darkModeDefault: import.meta.env.VITE_DARK_MODE_DEFAULT !== "false",
  showAgentProgress: true,
  showPomlTab: true,
  showEvaluationDetails: true,
};

// ── Agent Pipeline Configuration ───────────────────────────
// Pipeline order (docs/API.md). `name` is the backend key; colours come from
// the --agent-<name> tokens.

export const AGENTS = [
  { name: "sentinel", label: "Sentinel", description: "Classifies your task" },
  { name: "scout", label: "Scout", description: "Gathers best practices" },
  { name: "architect", label: "Architect", description: "Interviews you to fill the gaps" },
  { name: "forge", label: "Forge", description: "Drafts a sample answer" },
  { name: "scribe", label: "Scribe", description: "Writes the polished prompt" },
  { name: "judge", label: "Judge", description: "Evaluates the prompt" },
  { name: "mirror", label: "Mirror", description: "Turns feedback into fixes" },
  { name: "cipher", label: "Cipher", description: "Converts the prompt to POML" },
];

export const AGENT_LABELS = Object.fromEntries(AGENTS.map((a) => [a.name, a.label]));

/** Agents that POST /api/chat/{id}/rerun accepts. */
export const RERUNNABLE_AGENTS = ["sentinel", "scout", "forge", "scribe", "judge", "mirror", "cipher"];

// ── Pipeline Stages ────────────────────────────────────────

export const PIPELINE_STAGES = {
  idle: "idle",
  sentinel: "sentinel",
  scout: "scout",
  architect: "architect",
  forge: "forge",
  scribe: "scribe",
  judge: "judge",
  mirror: "mirror",
  cipher: "cipher",
  complete: "complete",
  error: "error",
};

export const STAGE_STATUS = {
  idle: "idle",
  running: "running",
  waiting: "waiting",
  complete: "complete",
  error: "error",
};

/** Button labels for PipelineStatus.available_actions (POST /advance). */
export const ACTION_LABELS = {
  continue: "Continue",
  finish_interview: "Finish interview",
  more_questions: "Ask me more",
  run_forge: "Draft a sample answer",
  skip_forge: "Skip draft",
  run_judge: "Evaluate",
  skip_judge: "Skip evaluation",
  improve: "Improve once more",
  finish: "Accept this prompt",
  run_cipher: "Generate POML",
  retry: "Retry",
};

/** Actions rendered as the primary (filled) button when present. */
export const PRIMARY_ACTIONS = ["continue", "finish", "retry", "finish_interview"];

// ── Prompt elements (prompt_ir / coverage) ─────────────────

export const ELEMENT_TIERS = [
  {
    key: "essential",
    label: "Essential",
    elements: ["role", "context", "goal", "task", "input_format", "output_format", "deliverables", "constraints"],
  },
  {
    key: "enhancement",
    label: "Enhancement",
    elements: ["reference_data", "examples", "style_tone", "audience", "formatting_rules", "reasoning_mode", "task_specific"],
  },
  {
    key: "elevation",
    label: "Elevation",
    elements: ["best_practices", "confidence_level", "model_settings"],
  },
];

// ── Special Chat Commands ──────────────────────────────────

export const COMMANDS = {
  done: "/done",
  finish: "/finish",
  complete: "/complete",
  generate: "/generate",
  more: "/more",
  questions: "/questions",
  retry: "/retry",
  restart: "/restart",
  reset: "/reset",
};

// ── Local Storage Keys ─────────────────────────────────────

export const STORAGE_KEYS = {
  accessToken: "pawni_access_token",
  refreshToken: "pawni_refresh_token",
  user: "pawni_user",
  theme: "pawni_theme",
  animationsEnabled: "pawni_animations_enabled",
};

// ── Misc ───────────────────────────────────────────────────

export const MAX_MESSAGE_LENGTH = 10000;
export const SESSION_POLL_INTERVAL = 2000; // ms (fallback status poll while the event stream is down)
export const SSE_BACKOFF_MIN = 1000; // ms
export const SSE_BACKOFF_MAX = 30000; // ms

// ── Routes ─────────────────────────────────────────────────

export const ROUTES = {
  home: "/",
  login: "/login",
  app: "/app",
  session: (id) => `/app/${id}`,
  admin: "/admin",
  privacy: "/privacy",
  terms: "/terms",
  cookies: "/cookies",
  acceptableUse: "/acceptable-use",
  aiDisclaimer: "/ai-disclaimer",
  dataRetention: "/data-retention",
};

export const LEGAL_LINKS = [
  { to: ROUTES.privacy, label: "Privacy Policy" },
  { to: ROUTES.terms, label: "Terms of Service" },
  { to: ROUTES.cookies, label: "Cookie Notice" },
  { to: ROUTES.acceptableUse, label: "Acceptable Use" },
  { to: ROUTES.aiDisclaimer, label: "AI Disclaimer" },
  { to: ROUTES.dataRetention, label: "Data Retention" },
];

// ── Project facts (legal pages, footer) ────────────────────

export const SITE = {
  name: "PAWNI",
  domain: "howdoiaskaquestion.com",
  url: "https://howdoiaskaquestion.com",
  contactEmail: "thepawniproject@gmail.com",
  operators: ["Sankar Balasubramanian", "Pawni Yadav", "Srinidhi Ranjini Girish"],
  operatorLinks: {
    "Sankar Balasubramanian": "https://www.linkedin.com/in/sankar4/",
    "Pawni Yadav": "https://www.linkedin.com/in/pawni-yadav/",
    "Srinidhi Ranjini Girish": "https://www.linkedin.com/in/srinidhi-r-g-052a32226/",
  },
  legalEffectiveDate: "2026-10-09",
  paper: {
    title:
      "Asking Questions the Right Way: A Multi-Agent Conversational System for Prompt Formulation in Complex Task Resolution",
    arxivId: "2608.01366",
    url: "https://arxiv.org/abs/2608.01366",
    authors: ["B. Sankar", "Pawni Yadav", "Srinidhi Ranjini Girish", "Amogh A. S"],
  },
};

export const CONSENT_STORAGE_KEY = "pawni_consent_v1";
